import React, { useMemo, useState, useEffect } from 'react';
import styled from 'styled-components';
import { AdminLayout } from '../components/AdminLayout';
import api, { apiService } from '../services/api';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  CreditCard,
  FileText,
  Download,
  Filter,
  BarChart3,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  Loader,
  Trash2,
  Ban
} from 'lucide-react';

const Container = styled.div`
  padding: 2rem;
  width: 100%;
  max-width: none;
  margin: 0;

  @media (max-width: 768px) {
    padding: 1.25rem 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #333;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: 768px) {
    width: 100%;
    flex-wrap: wrap;
  }
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'success' | 'outline' }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.2s ease;

  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #00d4aa 0%, #00b894 100%);
          color: white;
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
          }
        `;
      case 'success':
        return `
          background: #28a745;
          color: white;
          &:hover { background: #218838; }
        `;
      case 'outline':
        return `
          background: transparent;
          color: #00d4aa;
          border: 2px solid #00d4aa;
          &:hover {
            background: #00d4aa;
            color: white;
          }
        `;
      default:
        return `
          background: white;
          color: #333;
          border: 1px solid #e9ecef;
          &:hover { background: #f8f9fa; }
        `;
    }
  }}

  @media (max-width: 768px) {
    flex: 1 1 160px;
    justify-content: center;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div<{ $color?: string }>`
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: ${props => props.$color || '#00d4aa'};
  }
`;

const StatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const StatIcon = styled.div<{ $color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.$color}20;
  color: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatChange = styled.div<{ $positive?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props.$positive ? '#28a745' : '#dc3545'};
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  color: #666;
  font-size: 0.875rem;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #f8f9fa;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const CardTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const ChartContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  height: 240px;
  padding: 12px 4px 0;
  overflow-x: auto;
`;

const ChartColumn = styled.div`
  min-width: 68px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const ChartBars = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;
  height: 180px;
`;

const ChartBar = styled.div<{ $height: number; $color: string }>`
  width: 20px;
  height: ${props => props.$height}%;
  background: ${props => props.$color};
  border-radius: 6px 6px 0 0;
  transition: height 0.2s ease;
`;

const ChartLabel = styled.div`
  font-size: 0.75rem;
  color: #6c757d;
`;

const ChartLegend = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 12px;
  font-size: 0.85rem;
  color: #495057;
`;

const ChartLegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ChartLegendSwatch = styled.span<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: ${props => props.$color};
`;

const CardContent = styled.div`
  padding: 1.5rem;
`;

const ChartPlaceholder = styled.div`
  height: 300px;
  background: #f8f9fa;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  flex-direction: column;
  gap: 1rem;
`;

const TransactionsList = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

const TransactionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid #f8f9fa;

  &:last-child {
    border-bottom: none;
  }
`;

const TransactionIcon = styled.div<{ $type: string }>`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  ${props => {
    switch (props.$type) {
      case 'payment':
        return `background: #d4edda; color: #28a745;`;
      case 'refund':
        return `background: #f8d7da; color: #dc3545;`;
      case 'pending':
        return `background: #fff3cd; color: #ffc107;`;
      default:
        return `background: #f8f9fa; color: #666;`;
    }
  }}
`;

const TransactionDetails = styled.div`
  flex: 1;
`;

const TransactionDescription = styled.div`
  font-weight: 500;
  color: #333;
  margin-bottom: 0.25rem;
`;

const TransactionMeta = styled.div`
  color: #666;
  font-size: 0.875rem;
`;

const TransactionAmount = styled.div<{ $type: string }>`
  font-weight: 600;
  color: ${props => {
    switch (props.$type) {
      case 'payment': return '#28a745';
      case 'refund': return '#dc3545';
      default: return '#ffc107';
    }
  }};
`;

const FiltersContainer = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const FilterLabel = styled.label`
  font-weight: 500;
  color: #333;
  font-size: 0.875rem;
`;

const FilterInput = styled.input`
  padding: 0.5rem 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 6px;
  font-size: 0.875rem;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #00d4aa;
  }
`;

const FilterSelect = styled.select`
  padding: 0.5rem 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #00d4aa;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  @media (max-width: 768px) {
    min-width: 900px;
  }
`;

const TableWrapper = styled.div`
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const Th = styled.th`
  text-align: left;
  padding: 1rem 1.5rem;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #f8f9fa;
  background: #fafafa;
`;

const Td = styled.td`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f8f9fa;
  color: #666;
`;

const Badge = styled.span<{ $variant?: 'success' | 'warning' | 'danger' | 'info' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;

  ${props => {
    switch (props.$variant) {
      case 'success':
        return `background: #d4edda; color: #155724;`;
      case 'warning':
        return `background: #fff3cd; color: #856404;`;
      case 'danger':
        return `background: #f8d7da; color: #721c24;`;
      case 'info':
        return `background: #d1ecf1; color: #0c5460;`;
      default:
        return `background: #e9ecef; color: #495057;`;
    }
  }}
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  color: #00d4aa;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const IconButton = styled.button<{ $variant?: 'danger' | 'warning' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  background: transparent;

  ${props => {
    switch (props.$variant) {
      case 'danger':
        return `
          color: #dc3545;
          &:hover {
            background: #f8d7da;
          }
        `;
      case 'warning':
        return `
          color: #ffc107;
          &:hover {
            background: #fff3cd;
          }
        `;
      default:
        return `
          color: #666;
          &:hover {
            background: #f8f9fa;
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Modal = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const ModalBody = styled.div`
  margin-bottom: 1.5rem;
  color: #666;
  line-height: 1.6;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const ModalButton = styled.button<{ $variant?: 'danger' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => {
    switch (props.$variant) {
      case 'danger':
        return `
          background: #dc3545;
          color: white;
          &:hover {
            background: #c82333;
          }
        `;
      case 'secondary':
        return `
          background: #6c757d;
          color: white;
          &:hover {
            background: #5a6268;
          }
        `;
      default:
        return `
          background: #e9ecef;
          color: #333;
          &:hover {
            background: #dee2e6;
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

interface Transaction {
  id: number;
  company_name: string;
  description: string;
  amount: number;
  type: 'payment' | 'refund';
  status: 'completed' | 'pending' | 'failed';
  date: string;
  plan: string;
  payment_method?: string;
  mercadopago_payment_id?: string;
  payment_type?: string;
  paid_at?: string;
}

interface MonthlyRevenue {
  month: string;
  month_full: string;
  year: string;
  revenue: number;
  pending: number;
  total: number;
  payments_count: number;
}

export const AdminFinancialPage: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [statistics, setStatistics] = useState({
    total_revenue: 0,
    pending_revenue: 0,
    refunds: 0,
    transactions_count: 0,
    completed_transactions: 0,
    pending_transactions: 0,
    revenue_growth: 0
  });
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [monthlyRevenue, setMonthlyRevenue] = useState<MonthlyRevenue[]>([]);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoicePaymentId, setInvoicePaymentId] = useState('');

  useEffect(() => {
    loadFinancialData();
  }, []);

  const loadFinancialData = async () => {
    try {
      setIsLoading(true);

      const [statsResponse, transactionsResponse] = await Promise.all([
        api.getAdminFinancialStatistics(),
        api.getAdminFinancialTransactions()
      ]);

      const revenueResponse = await api.getAdminFinancialMonthlyRevenue();

      if (statsResponse.success) {
        setStatistics(statsResponse.data);
      }

      if (transactionsResponse.success) {
        setTransactions(transactionsResponse.data);
      }

      if (revenueResponse.success) {
        setMonthlyRevenue(revenueResponse.data || []);
      }

      setIsLoading(false);
    } catch (error) {
      console.error('Error loading financial data:', error);
      setIsLoading(false);
    }
  };

  const applyFilters = async () => {
    try {
      setIsLoadingTransactions(true);

      const params: any = {
        status: statusFilter,
        type: typeFilter
      };

      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const [statsResponse, transactionsResponse] = await Promise.all([
        api.getAdminFinancialStatistics(params),
        api.getAdminFinancialTransactions(params)
      ]);

      const revenueResponse = await api.getAdminFinancialMonthlyRevenue();

      if (statsResponse.success) {
        setStatistics(statsResponse.data);
      }

      if (transactionsResponse.success) {
        setTransactions(transactionsResponse.data);
      }

      if (revenueResponse.success) {
        setMonthlyRevenue(revenueResponse.data || []);
      }

      setIsLoadingTransactions(false);
    } catch (error) {
      console.error('Error applying filters:', error);
      setIsLoadingTransactions(false);
    }
  };

  const handleCancelClick = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setShowCancelModal(true);
  };

  const handleDeleteClick = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setShowDeleteModal(true);
  };

  const handleCancelPayment = async () => {
    if (!selectedTransaction) return;

    try {
      setIsProcessing(true);

      const response = await apiService.axiosInstance.post(`/admin/payments/${selectedTransaction.id}/cancel`);

      if (response.data.success) {
        // Recarregar dados
        await loadFinancialData();
        setShowCancelModal(false);
        setSelectedTransaction(null);
        alert('Cobrança cancelada com sucesso!');
      } else {
        alert('Erro ao cancelar cobrança: ' + (response.data.message || 'Erro desconhecido'));
      }
    } catch (error: any) {
      console.error('Error canceling payment:', error);
      alert('Erro ao cancelar cobrança: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeletePayment = async () => {
    if (!selectedTransaction) return;

    try {
      setIsProcessing(true);

      const response = await apiService.axiosInstance.delete(`/admin/payments/${selectedTransaction.id}`);

      if (response.data.success) {
        // Recarregar dados
        await loadFinancialData();
        setShowDeleteModal(false);
        setSelectedTransaction(null);
        alert('Cobrança excluída com sucesso!');
      } else {
        alert('Erro ao excluir cobrança: ' + (response.data.message || 'Erro desconhecido'));
      }
    } catch (error: any) {
      console.error('Error deleting payment:', error);
      alert('Erro ao excluir cobrança: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExportReport = async () => {
    try {
      const params: any = {
        status: statusFilter,
        type: typeFilter
      };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const response = await apiService.axiosInstance.get('/admin/financial/export', {
        params,
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `relatorio-financeiro-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error exporting report:', error);
      alert('Erro ao exportar relatorio: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleGenerateInvoice = async () => {
    if (!invoicePaymentId) {
      alert('Selecione um pagamento para gerar a fatura.');
      return;
    }

    try {
      setIsProcessing(true);
      const response = await apiService.axiosInstance.post('/admin/financial/invoices/generate', {
        payment_id: Number(invoicePaymentId)
      });

      if (response.data.success) {
        alert(response.data.message || 'Fatura gerada com sucesso!');
        setShowInvoiceModal(false);
        setInvoicePaymentId('');
      } else {
        alert('Erro ao gerar fatura: ' + (response.data.message || 'Erro desconhecido'));
      }
    } catch (error: any) {
      console.error('Error generating invoice:', error);
      alert('Erro ao gerar fatura: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsProcessing(false);
    }
  };

  // Filtrar apenas transações confirmadas para exibir nas recentes
  const recentTransactions = transactions
    .filter(transaction => transaction.status === 'completed')
    .slice(0, 10);

  const getTransactionIcon = (type: string, status: string) => {
    if (status === 'pending') return <Clock size={16} />;
    if (type === 'payment') return <CheckCircle size={16} />;
    if (type === 'refund') return <XCircle size={16} />;
    return <AlertCircle size={16} />;
  };

  const getStatusBadge = (status: string) => {
    const config = {
      completed: { variant: 'success' as const, icon: <CheckCircle size={12} />, label: 'Concluído' },
      pending: { variant: 'warning' as const, icon: <Clock size={12} />, label: 'Pendente' },
      failed: { variant: 'danger' as const, icon: <XCircle size={12} />, label: 'Falhou' }
    };

    const { variant, icon, label } = config[status as keyof typeof config] || config.pending;

    return (
      <Badge $variant={variant}>
        {icon}
        {label}
      </Badge>
    );
  };

  const revenueMax = useMemo(() => {
    return Math.max(0, ...monthlyRevenue.map(item => item.total));
  }, [monthlyRevenue]);

  if (isLoading) {
    return (
      <AdminLayout>
        <LoadingContainer>
          <Loader size={48} className="animate-spin" />
        </LoadingContainer>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Container>
      <Header>
        <Title>Módulo Financeiro</Title>
        <HeaderActions>
          <Button $variant="outline" onClick={handleExportReport}>
            <Download size={16} />
            Exportar Relatório
          </Button>
          <Button $variant="success" onClick={() => setShowInvoiceModal(true)}>
            <FileText size={16} />
            Gerar Fatura
          </Button>
        </HeaderActions>
      </Header>

      <StatsGrid>
        <StatCard $color="#28a745">
          <StatHeader>
            <StatIcon $color="#28a745">
              <DollarSign size={20} />
            </StatIcon>
            <StatChange $positive={statistics.revenue_growth >= 0}>
              {statistics.revenue_growth >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
              {statistics.revenue_growth >= 0 ? '+' : ''}{statistics.revenue_growth}%
            </StatChange>
          </StatHeader>
          <StatValue>R$ {statistics.total_revenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</StatValue>
          <StatLabel>Receita Confirmada</StatLabel>
        </StatCard>

        <StatCard $color="#ffc107">
          <StatHeader>
            <StatIcon $color="#ffc107">
              <Clock size={20} />
            </StatIcon>
            <StatChange>
              <Clock size={14} />
              {statistics.pending_transactions} pendentes
            </StatChange>
          </StatHeader>
          <StatValue>R$ {statistics.pending_revenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</StatValue>
          <StatLabel>Receita Pendente</StatLabel>
        </StatCard>

        <StatCard $color="#dc3545">
          <StatHeader>
            <StatIcon $color="#dc3545">
              <TrendingDown size={20} />
            </StatIcon>
            <StatChange>
              <TrendingDown size={14} />
              Reembolsos
            </StatChange>
          </StatHeader>
          <StatValue>R$ {statistics.refunds.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</StatValue>
          <StatLabel>Reembolsos</StatLabel>
        </StatCard>

        <StatCard $color="#00d4aa">
          <StatHeader>
            <StatIcon $color="#00d4aa">
              <CreditCard size={20} />
            </StatIcon>
            <StatChange $positive>
              <CheckCircle size={14} />
              {statistics.completed_transactions} completas
            </StatChange>
          </StatHeader>
          <StatValue>{statistics.transactions_count}</StatValue>
          <StatLabel>Total de Transações</StatLabel>
        </StatCard>
      </StatsGrid>

      <FiltersContainer>
        <FilterGroup>
          <FilterLabel>Data Inicial</FilterLabel>
          <FilterInput
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Data Final</FilterLabel>
          <FilterInput
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Status</FilterLabel>
          <FilterSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">Todos</option>
            <option value="completed">Concluído</option>
            <option value="pending">Pendente</option>
            <option value="failed">Falhou</option>
          </FilterSelect>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Tipo</FilterLabel>
          <FilterSelect value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="all">Todos</option>
            <option value="payment">Pagamento</option>
            <option value="refund">Reembolso</option>
          </FilterSelect>
        </FilterGroup>

        <Button
          $variant="primary"
          style={{ alignSelf: 'flex-end' }}
          onClick={applyFilters}
          disabled={isLoadingTransactions}
        >
          {isLoadingTransactions ? <Loader size={16} className="animate-spin" /> : <Filter size={16} />}
          Aplicar Filtros
        </Button>
      </FiltersContainer>

      <ContentGrid>
        <Card>
          <CardHeader>
            <CardTitle>Gráfico de Receitas</CardTitle>
          </CardHeader>
          <CardContent>
            {monthlyRevenue.length === 0 || revenueMax === 0 ? (
              <ChartPlaceholder>
                <BarChart3 size={48} style={{ opacity: 0.5 }} />
                <div>Sem dados de receita no período</div>
              </ChartPlaceholder>
            ) : (
              <>
                <ChartContainer>
                  {monthlyRevenue.map((item) => (
                    <ChartColumn key={`${item.month}-${item.year}`}>
                      <ChartBars>
                        <ChartBar
                          $height={Math.max(6, (item.revenue / revenueMax) * 100)}
                          $color="#00d4aa"
                        />
                        <ChartBar
                          $height={Math.max(6, (item.pending / revenueMax) * 100)}
                          $color="#ffc107"
                        />
                      </ChartBars>
                      <ChartLabel>{item.month}/{item.year}</ChartLabel>
                    </ChartColumn>
                  ))}
                </ChartContainer>
                <ChartLegend>
                  <ChartLegendItem>
                    <ChartLegendSwatch $color="#00d4aa" />
                    Receita confirmada
                  </ChartLegendItem>
                  <ChartLegendItem>
                    <ChartLegendSwatch $color="#ffc107" />
                    Receita pendente
                  </ChartLegendItem>
                </ChartLegend>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Transações Recentes</CardTitle>
          </CardHeader>
          <CardContent style={{ padding: 0 }}>
            <TransactionsList>
              {recentTransactions.map((transaction) => (
                <TransactionItem key={transaction.id}>
                  <TransactionIcon $type={transaction.type}>
                    {getTransactionIcon(transaction.type, transaction.status)}
                  </TransactionIcon>
                  <TransactionDetails>
                    <TransactionDescription>{transaction.description}</TransactionDescription>
                    <TransactionMeta>
                      {transaction.company_name} • {new Date(transaction.date).toLocaleDateString('pt-BR')}
                    </TransactionMeta>
                  </TransactionDetails>
                  <TransactionAmount $type={transaction.type}>
                    {transaction.type === 'refund' ? '-' : '+'}R$ {transaction.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </TransactionAmount>
                </TransactionItem>
              ))}
            </TransactionsList>
          </CardContent>
        </Card>
      </ContentGrid>

      <Card>
        <CardHeader>
          <CardTitle>Todas as Transações ({transactions.length})</CardTitle>
        </CardHeader>
        {isLoadingTransactions ? (
          <LoadingContainer>
            <Loader size={32} className="animate-spin" />
          </LoadingContainer>
        ) : (
          <>
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th>Data</Th>
                    <Th>Empresa</Th>
                    <Th>Descrição</Th>
                    <Th>Plano</Th>
                    <Th>Tipo</Th>
                    <Th>Status</Th>
                    <Th>Valor</Th>
                    <Th>Ações</Th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction) => (
                    <tr key={transaction.id}>
                      <Td>{new Date(transaction.date).toLocaleDateString('pt-BR')}</Td>
                      <Td>{transaction.company_name}</Td>
                      <Td>{transaction.description}</Td>
                      <Td>{transaction.plan}</Td>
                      <Td style={{ textTransform: 'capitalize' }}>{transaction.type === 'payment' ? 'Pagamento' : 'Reembolso'}</Td>
                      <Td>{getStatusBadge(transaction.status)}</Td>
                      <Td>
                        <TransactionAmount $type={transaction.type}>
                          {transaction.type === 'refund' ? '-' : ''}R$ {transaction.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </TransactionAmount>
                      </Td>
                      <Td>
                        <ActionButtons>
                          {transaction.status === 'pending' && (
                            <IconButton
                              $variant="warning"
                              onClick={() => handleCancelClick(transaction)}
                              title="Cancelar cobrança"
                            >
                              <Ban size={16} />
                            </IconButton>
                          )}
                          <IconButton
                            $variant="danger"
                            onClick={() => handleDeleteClick(transaction)}
                            title="Excluir cobrança"
                          >
                            <Trash2 size={16} />
                          </IconButton>
                        </ActionButtons>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>

            {transactions.length === 0 && (
              <div style={{ padding: '3rem', textAlign: 'center', color: '#666' }}>
                <FileText size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                <div>Nenhuma transação encontrada</div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Modal para Cancelar Cobrança */}
      <Modal $isOpen={showCancelModal} onClick={() => !isProcessing && setShowCancelModal(false)}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <Ban size={24} color="#ffc107" />
            <ModalTitle>Cancelar Cobrança</ModalTitle>
          </ModalHeader>
          <ModalBody>
            {selectedTransaction && (
              <>
                <p>Tem certeza que deseja cancelar esta cobrança?</p>
                <div style={{ marginTop: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                  <strong>Empresa:</strong> {selectedTransaction.company_name}<br />
                  <strong>Descrição:</strong> {selectedTransaction.description}<br />
                  <strong>Valor:</strong> R$ {selectedTransaction.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}<br />
                  <strong>Data:</strong> {new Date(selectedTransaction.date).toLocaleDateString('pt-BR')}
                </div>
                <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#dc3545' }}>
                  <strong>Atenção:</strong> Esta ação irá cancelar a cobrança no sistema e no MercadoPago (se aplicável).
                </p>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <ModalButton onClick={() => setShowCancelModal(false)} disabled={isProcessing}>
              Voltar
            </ModalButton>
            <ModalButton
              $variant="secondary"
              onClick={handleCancelPayment}
              disabled={isProcessing}
            >
              {isProcessing ? <><Loader size={16} className="animate-spin" /> Processando...</> : 'Sim, Cancelar'}
            </ModalButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal para Excluir Cobrança */}
      <Modal $isOpen={showDeleteModal} onClick={() => !isProcessing && setShowDeleteModal(false)}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <Trash2 size={24} color="#dc3545" />
            <ModalTitle>Excluir Cobrança</ModalTitle>
          </ModalHeader>
          <ModalBody>
            {selectedTransaction && (
              <>
                <p>Tem certeza que deseja excluir esta cobrança permanentemente?</p>
                <div style={{ marginTop: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                  <strong>Empresa:</strong> {selectedTransaction.company_name}<br />
                  <strong>Descrição:</strong> {selectedTransaction.description}<br />
                  <strong>Valor:</strong> R$ {selectedTransaction.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}<br />
                  <strong>Data:</strong> {new Date(selectedTransaction.date).toLocaleDateString('pt-BR')}<br />
                  <strong>Status:</strong> {selectedTransaction.status}
                </div>
                <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#dc3545' }}>
                  <strong>Atenção:</strong> Esta ação é irreversível e removerá permanentemente o registro da cobrança do sistema.
                </p>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <ModalButton onClick={() => setShowDeleteModal(false)} disabled={isProcessing}>
              Voltar
            </ModalButton>
            <ModalButton
              $variant="danger"
              onClick={handleDeletePayment}
              disabled={isProcessing}
            >
              {isProcessing ? <><Loader size={16} className="animate-spin" /> Processando...</> : 'Sim, Excluir'}
            </ModalButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal para Gerar Fatura */}
      <Modal $isOpen={showInvoiceModal} onClick={() => !isProcessing && setShowInvoiceModal(false)}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <FileText size={24} color="#28a745" />
            <ModalTitle>Gerar Fatura</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <p>Selecione o pagamento para gerar a fatura.</p>
            <FilterSelect
              style={{ marginTop: '1rem' }}
              value={invoicePaymentId}
              onChange={(e) => setInvoicePaymentId(e.target.value)}
            >
              <option value="">Selecione um pagamento</option>
              {transactions
                .filter((transaction) => transaction.type === 'payment')
                .map((transaction) => (
                  <option key={transaction.id} value={transaction.id}>
                    #{transaction.id} • {transaction.company_name} • R$ {transaction.amount.toLocaleString('pt-BR')}
                  </option>
                ))}
            </FilterSelect>
          </ModalBody>
          <ModalFooter>
            <ModalButton onClick={() => setShowInvoiceModal(false)} disabled={isProcessing}>
              Voltar
            </ModalButton>
            <ModalButton
              $variant="secondary"
              onClick={handleGenerateInvoice}
              disabled={isProcessing}
            >
              {isProcessing ? <><Loader size={16} className="animate-spin" /> Processando...</> : 'Gerar'}
            </ModalButton>
          </ModalFooter>
        </ModalContent>
      </Modal>
      </Container>
    </AdminLayout>
  );
};
